import React, { useRef } from "react";
import { Button, Select, SelectItem, TextInput } from "@carbon/react";
import { Add } from "@carbon/icons-react";
import i18next from "../../../i18n";
import { MaterialResponse, SiteResponse } from "../../../types";
import { MEASUREMENT_UNIT_LIST } from "../../../utils";
import MaterialAutocompleteCell from "../MaterialAutocompleteCell";

/**
 * What the composer is holding before it becomes an item. Owned by the page so a freshly created
 * material can be dropped straight into it. Deliberately NOT an RHF field: it is a transient staging
 * area, not part of the submitted request — "Add item" is what appends it to the RHF field array.
 */
export type ComposerState = {
  material: MaterialResponse | null;
  quantity: number | null;
  measurementUnit: string;
  siteId: string;
};

export const emptyComposer = (siteId = ""): ComposerState => ({
  material: null,
  quantity: null,
  measurementUnit: "",
  siteId,
});

type Props = {
  value: ComposerState;
  onChange: (next: ComposerState) => void;
  onSubmit: () => void;
  onCreateMaterial: (searchTerm: string) => void;
  sites: SiteResponse[];
  searchInputRef?: (el: HTMLInputElement | null) => void;
};

export const canComposerSubmit = (value: ComposerState): boolean =>
  !!value.material && !!value.quantity && value.quantity > 0 && !!value.siteId;

// De-dupe measurement units by name (MEASUREMENT_UNIT_LIST carries two entries named "M") so the
// Carbon SelectItem keys don't collide — same handling the migrated create-material form uses.
const unitNames = Array.from(
  new Map((MEASUREMENT_UNIT_LIST ?? []).map((u) => [u.name, u.name])).keys()
);

const ItemComposer: React.FC<Props> = ({
  value,
  onChange,
  onSubmit,
  onCreateMaterial,
  sites,
  searchInputRef,
}) => {
  const quantityRef = useRef<HTMLInputElement | null>(null);
  const canSubmit = canComposerSubmit(value);

  const handleMaterialSelected = (material: MaterialResponse) => {
    onChange({
      ...value,
      material,
      measurementUnit: value.measurementUnit || material.measurementUnit || "",
      siteId: material.deliveryPlace?.id || value.siteId,
    });
    setTimeout(() => quantityRef.current?.focus(), 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && canSubmit) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <section
      aria-labelledby="composer-heading"
      className="new-request-composer"
    >
      <h2 id="composer-heading" className="new-request-section-title">
        {i18next.t("purchaseRequests.items.composerTitle")}
      </h2>

      <div className="cds-form-surface new-request-composer__surface">
        <div className="new-request-composer__row">
          <div className="new-request-composer__material">
            <MaterialAutocompleteCell
              value={value.material?.globalMaterialName ?? ""}
              isSelected={!!value.material}
              inputRef={searchInputRef}
              onSelect={handleMaterialSelected}
              onClear={() => onChange(emptyComposer(value.siteId))}
              onCreateNew={onCreateMaterial}
            />
          </div>

          <TextInput
            id="composer-quantity"
            ref={quantityRef}
            type="number"
            min={0}
            labelText={i18next.t("purchaseRequests.items.columns.quantity")}
            hideLabel
            placeholder={i18next.t("purchaseRequests.items.columns.quantity")}
            value={value.quantity ?? ""}
            onKeyDown={handleKeyDown}
            onChange={(e) =>
              onChange({
                ...value,
                quantity: e.target.value === "" ? null : Number(e.target.value),
              })
            }
          />

          <Select
            id="composer-unit"
            labelText={i18next.t(
              "purchaseRequests.items.columns.measurementUnit"
            )}
            hideLabel
            value={value.measurementUnit}
            onKeyDown={handleKeyDown}
            onChange={(e) =>
              onChange({ ...value, measurementUnit: e.target.value })
            }
          >
            <SelectItem
              value=""
              text={i18next.t("purchaseRequests.items.unitPlaceholder")}
            />
            {unitNames.map((name) => (
              <SelectItem key={name} value={name} text={name} />
            ))}
          </Select>

          <Select
            id="composer-site"
            labelText={i18next.t("purchaseRequests.items.columns.site")}
            hideLabel
            value={value.siteId}
            onKeyDown={handleKeyDown}
            onChange={(e) => onChange({ ...value, siteId: e.target.value })}
          >
            <SelectItem
              value=""
              text={i18next.t("purchaseRequests.items.selectSite")}
            />
            {sites.map((site) => (
              <SelectItem key={site.id} value={site.id} text={site.name} />
            ))}
          </Select>

          <Button
            renderIcon={Add}
            onClick={onSubmit}
            disabled={!canSubmit}
            className="new-request-composer__add"
          >
            {i18next.t("purchaseRequests.items.add")}
          </Button>
        </div>

        <div className="new-request-composer__create">
          <span className="new-request-composer__create-hint">
            {i18next.t("purchaseRequests.items.notInCatalog")}
          </span>
          <Button
            kind="tertiary"
            size="sm"
            renderIcon={Add}
            onClick={() => onCreateMaterial("")}
          >
            {i18next.t("purchaseRequests.items.createMaterialCta")}
          </Button>
        </div>

        <p className="new-request-composer__hint">
          {i18next.t("purchaseRequests.items.composerHint")}
        </p>
      </div>
    </section>
  );
};

export default ItemComposer;
